import React from 'react';

const HowWeHelp = () => (
  <div className="container mx-auto px-4 py-8">
    <h1 className="text-3xl font-bold mb-6">How We Can Help You</h1>

    <p className="mb-6">
      <strong>Quantum computing is moving from research labs to board-room agendas—fast.</strong> If your organisation must keep data confidential for the next decade, or if NIS 2 and ISO 27001 apply to you, the time to act is now. Our consultancy exists for one purpose: <strong>to help you migrate safely and efficiently to quantum-resistant cryptography.</strong>
    </p>

    <h2 className="text-2xl font-bold mb-4">The Ways We Help You</h2>

    <div className="mb-8">
      <div className="bg-gray-100 p-6 rounded-lg mb-4">
        <h3 className="text-xl font-bold mb-2">1 · PQC Quick Scan & Roadmap</h3>
        <p><em>What it delivers to you:</em> a two-week assessment of every public endpoint, your internal PKI, and key-management workflow. You receive a maturity score plus a phased migration plan through 2030, based on NIST FIPS 203 and ETSI quantum-safe profiles.</p>
      </div>

      <div className="bg-gray-100 p-6 rounded-lg mb-4">
        <h3 className="text-xl font-bold mb-2">2 · ISO 27001 "Quantum Risk" Integration</h3>
        <p><em>What it delivers to you:</em> ready-to-audit risk analysis, Statement-of-Applicability updates, and continuous-improvement inputs that make quantum threats tangible—without derailing daily operations.</p>
      </div>

      <div className="bg-gray-100 p-6 rounded-lg mb-4">
        <h3 className="text-xl font-bold mb-2">3 · Migration & Implementation Support</h3>
        <p><em>What it delivers to you:</em> hands-on pilots of hybrid TLS 1.3 (ML-KEM + X25519), .NET code reviews, performance benchmarks, and change-management coaching—so your teams adopt new crypto with confidence.</p>
      </div>

      <div className="bg-gray-100 p-6 rounded-lg mb-4">
        <h3 className="text-xl font-bold mb-2">4 · Training & Awareness</h3>
        <p><em>What it delivers to you:</em> executive briefings that translate quantum risk into business terms, plus deep-dive labs for DevSecOps. Every course uses findings from <em>your</em> Quick Scan, turning theory into immediate action.</p>
      </div>

      <div className="bg-gray-100 p-6 rounded-lg mb-4">
        <h3 className="text-xl font-bold mb-2">5 · Supplier & Cloud Assurance</h3>
        <p><em>What it delivers to you:</em> objective assessments of third-party services against ENISA and ETSI guidance, plus contractual clauses that keep your supply chain as agile as your own systems.</p>
      </div>

      <p className="italic text-gray-600 mt-2">
        (A continuous monitoring platform is in active development; early-access clients will be invited later this year.)
      </p>
    </div>

    <h2 className="text-2xl font-bold mb-4">How We Guide You—Step by Step</h2>

    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
      <div className="bg-gray-100 p-4 rounded-lg">
        <p className="font-bold">Discover</p>
        <p>Automated scans, stakeholder interviews, and risk mapping</p>
      </div>
      <div className="bg-gray-100 p-4 rounded-lg">
        <p className="font-bold">Design</p>
        <p>A tailored roadmap that balances urgency, budget, and business value</p>
      </div>
      <div className="bg-gray-100 p-4 rounded-lg">
        <p className="font-bold">Deploy</p>
        <p>Supervised pilots, secure configuration, and knowledge transfer</p>
      </div>
      <div className="bg-gray-100 p-4 rounded-lg">
        <p className="font-bold">Demonstrate</p>
        <p>Audit-ready evidence for ISO 27001, board reports, and client assurances</p>
      </div>
    </div>

    <h2 className="text-2xl font-bold mb-4">Why Clients Choose Us</h2>

    <ul className="list-disc pl-6 mb-8">
      <li className="mb-2"><strong>Depth you can trust</strong> – 14 years of secure .NET engineering and back-end design</li>
      <li className="mb-2"><strong>Compliance fluency</strong> – multiple successful ISO 27001 certifications for SMEs in finance, healthcare, and IT services</li>
      <li className="mb-2"><strong>Tool-powered insight</strong> – proprietary scanning engine for fast, repeatable results</li>
      <li className="mb-2"><strong>Vendor-neutral advice</strong> – recommendations driven solely by your risk profile</li>
    </ul>

    <h2 className="text-2xl font-bold mb-4">Meet the Founder</h2>

    <p className="mb-8">
      Our practice is led by a Dutch former scientist turned senior software engineer. Scientific rigour, code craftsmanship, and hands-on ISO 27001 expertise combine to give you advice that is both precise <em>and</em> practicable.
    </p>

    <h2 className="text-2xl font-bold mb-4">Your Next Move</h2>

    <ol className="list-decimal pl-6 mb-8">
      <li className="mb-2"><strong>Book a complimentary consultation</strong>—we map your cryptographic landscape.</li>
      <li className="mb-2"><strong>Schedule a PQC Quick Scan</strong>—actionable results in just two weeks.</li>
      <li className="mb-2"><strong>Secure your future</strong>—stay ahead of quantum threats before they become today's reality.</li>
    </ol>

    <div className="bg-blue-100 p-6 rounded-lg mb-8">
      <p className="text-lg">
        Ready to begin? <a href="/contact-us" className="text-blue-600 hover:underline">Contact us here</a>
      </p>
    </div>
  </div>
);

export default HowWeHelp;
